<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { WorkspaceListDTO } from '@/types/workspace'

import { getWorkspaces } from '@/services/workspaceService'

definePage({
  meta: {
    requiresAuth: true,
    requiresInternalAccess: true,
  },
})

const router = useRouter()
const { t, d } = useI18n()

const { adminModeEnabled } = useAdminMode()

// State management
const workspaces = ref<WorkspaceListDTO[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

/**
 * Fetches the list of workspaces from the service and updates the state.
 */
async function fetchWorkspaces(adminView = false) {
  loading.value = true
  error.value = null
  try {
    workspaces.value = await getWorkspaces(adminView)
  } catch (err) {
    console.error('Error fetching workspaces:', err)
    error.value = 'Failed to load workspaces.'
  } finally {
    loading.value = false
  }
}

// Actions
const goToCreatePage = () => {
  router.push('/workspaces/create')
}

const viewWorkspace = (org: WorkspaceListDTO) => {
  router.push(`/workspaces/${org.id}`)
}

// Initialize data on component mount using the composable's fetch function
onMounted(() => {
  fetchWorkspaces(adminModeEnabled.value)
})

// Watch admin mode changes and reload workspaces
watch(adminModeEnabled, () => {
  fetchWorkspaces(adminModeEnabled.value)
})
</script>

<template>
  <div class="content-wrap">
    <div class="mb-4 flex flex-col items-center justify-between md:flex-row">
      <h1 class="text-2xl font-bold">
        {{ t('pages.workspaces.title') }}
      </h1>
      <div class="mt-3 flex gap-2 md:mt-0">
        <UButton
          icon="i-lucide-plus"
          :label="t('pages.workspaces.actions.create')"
          color="success"
          @click="goToCreatePage"
        />
      </div>
    </div>

    <!-- Display error message -->
    <div
      v-if="error"
      class="mb-4 rounded border border-red-400 bg-red-100 p-4 text-red-700 dark:border-red-500/40 dark:bg-red-500/15 dark:text-red-200"
    >
      {{ t('common.messages.errorGeneral') }}: {{ error }}
    </div>
    <div class="mycard">
      <!-- Workspaces Simple Table -->
      <div v-if="loading" class="p-8 text-center">
        <UIcon name="i-lucide-loader-circle" class="animate-spin text-2xl" />
        <p class="mt-2">
          {{ t('common.labels.loading') }}
        </p>
      </div>

      <div
        v-else-if="!workspaces?.length"
        class="mx-auto max-w-4xl px-4 py-10 text-neutral-700 dark:text-neutral-300"
      >
        <div class="mb-8 text-center">
          <UIcon name="i-lucide-building-2" class="mb-4 text-4xl text-[var(--app-text-primary)]" />
          <h2 class="mb-3 text-xl font-semibold text-neutral-950 dark:text-white">
            {{ t('pages.workspaces.empty.title') }}
          </h2>
          <p class="mx-auto max-w-2xl leading-relaxed">
            {{ t('pages.workspaces.empty.description') }}
          </p>
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <section class="rounded-md border border-neutral-200 p-5 dark:border-neutral-700">
            <h3 class="mb-3 font-semibold text-neutral-950 dark:text-white">
              {{ t('pages.workspaces.empty.availableTitle') }}
            </h3>
            <ul class="space-y-2">
              <li class="flex gap-2">
                <UIcon name="i-lucide-check" class="mt-1 text-[var(--app-text-primary)]" />
                <span>{{ t('pages.workspaces.empty.available.eventManagement') }}</span>
              </li>
              <li class="flex gap-2">
                <UIcon name="i-lucide-check" class="mt-1 text-[var(--app-text-primary)]" />
                <span>{{ t('pages.workspaces.empty.available.sharedGroups') }}</span>
              </li>
              <li class="flex gap-2">
                <UIcon name="i-lucide-check" class="mt-1 text-[var(--app-text-primary)]" />
                <span>{{ t('pages.workspaces.empty.available.sharedRules') }}</span>
              </li>
              <li class="flex gap-2">
                <UIcon name="i-lucide-check" class="mt-1 text-[var(--app-text-primary)]" />
                <span>{{ t('pages.workspaces.empty.available.members') }}</span>
              </li>
            </ul>
          </section>

          <section class="rounded-md border border-neutral-200 p-5 dark:border-neutral-700">
            <h3 class="mb-3 font-semibold text-neutral-950 dark:text-white">
              {{ t('pages.workspaces.empty.exclusiveTitle') }}
            </h3>
            <ul class="space-y-2">
              <li class="flex gap-2">
                <UIcon name="i-lucide-lock" class="mt-1 text-[var(--app-text-primary)]" />
                <span>{{ t('pages.workspaces.empty.exclusive.fees') }}</span>
              </li>
              <li class="flex gap-2">
                <UIcon name="i-lucide-lock" class="mt-1 text-[var(--app-text-primary)]" />
                <span>{{ t('pages.workspaces.empty.exclusive.invites') }}</span>
              </li>
              <li class="flex gap-2">
                <UIcon name="i-lucide-lock" class="mt-1 text-[var(--app-text-primary)]" />
                <span>{{ t('pages.workspaces.empty.exclusive.subheadings') }}</span>
              </li>
              <li class="flex gap-2">
                <UIcon name="i-lucide-lock" class="mt-1 text-[var(--app-text-primary)]" />
                <span>{{ t('pages.workspaces.empty.exclusive.templates') }}</span>
              </li>
            </ul>
          </section>
        </div>

        <div class="mt-8 flex justify-center">
          <UButton
            icon="i-lucide-plus"
            :label="t('pages.workspaces.actions.create')"
            @click="goToCreatePage"
          />
        </div>
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b border-neutral-300 dark:border-neutral-600">
              <th class="p-4 text-left font-semibold text-neutral-700 dark:text-neutral-200">
                {{ t('pages.workspaces.fields.name') }}
              </th>
              <th class="p-4 text-right font-semibold text-neutral-700 dark:text-neutral-200">
                {{ t('pages.workspaces.fields.createdAt') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="org in workspaces"
              :key="org.id"
              class="border-b border-neutral-200 transition-colors hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
            >
              <td class="p-4">
                <div>
                  <button
                    class="block cursor-pointer text-left font-medium text-[var(--app-text-primary)] hover:text-primary-600"
                    @click="viewWorkspace(org)"
                  >
                    {{ org.nameDE }}
                  </button>
                </div>
              </td>
              <td class="p-4">
                <div class="flex items-center justify-end gap-2">
                  <UIcon name="i-lucide-clock" class="text-muted" />
                  <span class="text-sm">{{ d(org.createdAt, 'datetime') }}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
